import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Button, Link, useRecipe, useSlotRecipe} from '@chakra-ui/react3'
import useNavigation from '../../../../hooks/use-navigation'
import {convertToRelativePath} from '../../../../utils/url'
import {isExternalLink} from '../../../../utils/utils'
import {normalizeString, sanitizeAnchorTarget} from '../../../utils/utils'
import ctaSlotRecipe from './index.recipe'
import Icon from '../icon'

/**
 * Call-to-Action button component for the Experience Leap CMS.
 * Supports primary, secondary, and text variants with accessibility features.
 * Intended for use within the Leap CMS modules.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {string} props.label - The text content to display on the button.
 * @param {('primary'|'secondary'|'text')} [props.variant='primary'] - Button style variant.
 * @param {string} [props.screenReader] - Accessibility label for screen readers (aria-label).
 * @param {Object} [props.action] - Action object with type and value properties.
 * @param {('category'|'content'|'product'|'url'|'anchor')} [props.action.type] - Type of action to perform.
 * @param {string} [props.action.value] - The value for the action (URL, product ID, etc.).
 * @param {boolean} [props.openInNewTab=false] - Opens the link in a new tab when true.
 * @param {function} [props.onClick] - Optional click handler function. Can prevent default handling
 * @returns {JSX.Element} Rendered CTA button element.
 */
const CTA = ({
    label,
    variant = 'primary',
    screenReader,
    action,
    openInNewTab = false,
    onClick: _onClick,
    icon,
    iconPosition,
    ...rest
}) => {
    // use relative links instead for DEV/STG/PRD to handle mobify and local development environments
    const linkHref = useMemo(() => {
        if (action?.type === 'anchor') {
            return sanitizeAnchorTarget(action.value)
        }
        return convertToRelativePath(action?.value)
    }, [action?.type, action?.value])
    const navigate = useNavigation()

    // use slot recipe to test the approach, altough in this case a simple useRecipe would be sufficient
    const recipe = useSlotRecipe({recipe: ctaSlotRecipe})
    // chakra recommends to use recipe.splitVariantProps(props) to split props and parent styles - drawback is function interface becomes less clean
    // https://chakra-ui.com/docs/theming/recipes#splitvariantprops
    // for now keep it simple as the recipe is internal for the component
    const styles = useMemo(() => recipe({variant}), [recipe, variant])
    // get the button recipe to split the button props and styles
    const buttonRecipe = useRecipe({key: 'button'})
    const [buttonProps, buttonStyles] = useMemo(
        () => buttonRecipe.splitVariantProps(styles.button),
        [styles.button, buttonRecipe]
    )
    // get the link recipe to split the link props and styles
    const linkRecipe = useRecipe({key: 'link'})
    const [linkProps, linkStyles] = useMemo(
        () => linkRecipe.splitVariantProps(styles.link),
        [styles.link, linkRecipe]
    )

    const onClick = (e) => {
        // Call any provided onClick handler first
        if (_onClick) {
            _onClick(e)
            // and short-circuit if event was handled/prevented
            if (e.defaultPrevented) {
                return
            }
        }

        if (!linkHref) {
            e.preventDefault()
            return
        }

        // Handle different action types
        switch (action.type) {
            case 'url':
            case 'anchor':
                // For url and anchor types, use default browser handling
                return

            case 'product':
            case 'category':
            case 'content': {
                if (isExternalLink(linkHref)) {
                    // For external links, fallback to default browser handling
                    return
                }

                if (!openInNewTab) {
                    e.preventDefault()
                }
                // For internal links, use client-side navigation
                navigate(linkHref)
                return
            }

            default:
                // Fallback: leave default handling for unknown types
                return
        }
    }

    // do not render if no label is provided
    if (!label) {
        return null
    }

    // Use Link component for text variant
    if (variant === 'text') {
        return (
            <Link
                {...linkProps}
                css={linkStyles}
                href={linkHref}
                aria-label={normalizeString(screenReader) || undefined}
                onClick={onClick}
                {...(openInNewTab && {
                    target: '_blank',
                    rel: 'noopener noreferrer'
                })}
                {...rest}
            >
                {label}
            </Link>
        )
    }

    return (
        <Button
            // button props are spread
            {...buttonProps}
            // button styles are applied vie css prop
            css={buttonStyles}
            {...(linkHref && {as: 'a'})}
            href={linkHref}
            aria-label={normalizeString(screenReader) || undefined}
            onClick={onClick}
            {...(openInNewTab && {
                target: '_blank',
                rel: 'noopener noreferrer'
            })}
            {...rest}
        >
            {iconPosition === 'left' && <Icon {...icon} />}
            {label}
            {iconPosition === 'right' && <Icon {...icon} />}
        </Button>
    )
}

CTA.propTypes = {
    label: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(['primary', 'secondary', 'text']),
    screenReader: PropTypes.string,
    action: PropTypes.shape({
        type: PropTypes.oneOf(['category', 'content', 'product', 'url', 'anchor']),
        value: PropTypes.string
    }),
    openInNewTab: PropTypes.bool,
    onClick: PropTypes.func,
    icon: PropTypes.object,
    iconPosition: PropTypes.string
}

export default CTA
